import React, { useState, useRef, useEffect } from 'react';
import { Col, Row, Modal, Button } from 'react-bootstrap';
import { useTable, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import Table from './Table';
import ControlsSearch from './ControlsSearch';
import ControlsPageSize from './ControlsPageSize';
import TablePagination from './TablePagination';
import { useHistory } from 'react-router-dom';
import Plyr from 'plyr-react';
import YouTube from 'react-youtube';

function extractVideoId({ url }) {
  if (!url) return null;
  
  try {
    const parsedUrl = new URL(url);
    
    // Handle YouTube URLs
    if (parsedUrl.hostname.includes('youtube.com') || parsedUrl.hostname.includes('youtu.be')) {
      const regex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      const match = url.match(regex);
      return match ? match[1] : null;
    }
    
    // Handle Vimeo URLs
    if (parsedUrl.hostname.includes('vimeo.com')) {
      const parts = parsedUrl.pathname.split('/');
      const videoId = parts[parts.length - 1].replace(/[^\d]/g, '');
      return videoId;
    }
    
    // Handle direct video file URLs (MP4, WebM, etc.)
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const hasVideoExtension = videoExtensions.some(ext => 
      parsedUrl.pathname.toLowerCase().includes(ext)
    );
    
    if (hasVideoExtension) {
      return url; // Return the full URL for direct video files
    }
    
    return null;
  } catch (e) {
    console.error('Error parsing video URL:', e);
    return null;
  }
}

// Helper function to detect video platform
function getVideoPlatform(url) {
  if (!url) return null;
  
  try {
    const parsedUrl = new URL(url);
    
    if (parsedUrl.hostname.includes('youtube.com') || parsedUrl.hostname.includes('youtu.be')) {
      return 'youtube';
    }
    
    if (parsedUrl.hostname.includes('vimeo.com')) {
      return 'vimeo';
    }
    
    // Check for direct video file extensions
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const hasVideoExtension = videoExtensions.some(ext => 
      parsedUrl.pathname.toLowerCase().includes(ext)
    );
    
    if (hasVideoExtension) {
      return 'video'; // Direct video file
    }
    
    return null;
  } catch (e) {
    return null;
  }
}

const PurePlyr = React.memo(({ url }) => {
  const ref = useRef();
  const videoId = extractVideoId({ url });
  const platform = getVideoPlatform(url);

  useEffect(() => {
    // Access the plyr instance and play the video
    // if (ref.current && ref.current.plyr) {
    //   ref?.current?.plyr?.play();
    // }
  }, []);

  const options = {
    // autoplay: true,
  };

  // Only create video source if we have a valid video ID and platform
  if (!videoId || !platform) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">Invalid or unsupported video URL</p>
      </div>
    );
  }

  // Handle direct video files differently
  if (platform === 'video') {
    const videoSrc = {
      type: 'video',
      sources: [{ src: videoId, type: 'video/mp4' }],
      poster: '/img/video/video-large.webp',
    };
    return <Plyr source={videoSrc} ref={ref} options={options} />;
  }

  // Handle YouTube and Vimeo
  const videoSrc = {
    type: 'video',
    sources: [{ src: videoId, provider: platform }],
    poster: '/img/video/video-large.webp',
  };

  return <Plyr source={videoSrc} ref={ref} options={options} />;
});

const BoxedVariationsStripeParts = ({ data, lesson }) => {
  const history = useHistory();
  const [largeLeftModalExample, setLargeLeftModalExample] = useState(false);
  const [liveVideoID, setLiveVideoID] = useState('');
  const [animatedVideoID, setAnimatedVideoID] = useState('');

  const columns = React.useMemo(() => {
    return [
      {
        Header: 'Lesson Name',
        accessor: 'part',
        sortable: true,
        headerClassName: 'text-muted text-small text-uppercase w-30',
        Cell: ({ cell, row }) => {

          return (
            <button
              className="list-item-heading body nav-link"
              onClick={() => {
                setLargeLeftModalExample(true);
                setLiveVideoID(row.original.live);
                setAnimatedVideoID(row.original.animation);
              }}
            >
              {lesson} - {cell.value}
            </button>
          );
        },
      },
    ];
  }, []);

  useEffect(() => {
  }, [liveVideoID, animatedVideoID]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10, sortBy: [{ id: 'name', desc: true }] },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const opts = {
    height: '390',
    width: '640',
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 1,
      controls: 0,
    },
  };

  return (
    <Row>
      <Modal
        className="modal-left"
        show={largeLeftModalExample}
        onHide={() => setLargeLeftModalExample(false)}
      >
        <Modal.Body>
          <h2>Live Video</h2>
          {liveVideoID && <PurePlyr url={liveVideoID} />}
          <h2>Animated Video</h2>
          {animatedVideoID && <PurePlyr url={animatedVideoID} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLargeLeftModalExample(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Col sm="12" md="5" lg="3" xxl="2" className="mb-1">
        <div className="d-inline-block float-md-start me-1 search-input-container w-100 border border-separator bg-foreground search-sm">
          <ControlsSearch tableInstance={tableInstance} />
        </div>
      </Col>
      <Col sm="12" md="7" lg="9" xxl="10" className="text-end mb-1">
        <div className="d-inline-block">
          <ControlsPageSize tableInstance={tableInstance} />
        </div>
      </Col>

      <Col xs="12">
        <Table className="react-table rows stripe" tableInstance={tableInstance} />
      </Col>
      <Col xs="12">
        <TablePagination tableInstance={tableInstance} />
      </Col>
    </Row>
  );
};

export default BoxedVariationsStripeParts;
